import React, { useState } from 'react';
import { Table, Spinner, Button, Badge } from 'react-bootstrap';
import { Download, Award } from 'lucide-react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const formatCurrency = (n) =>
  new Intl.NumberFormat('vi-VN', { maximumFractionDigits: 0 }).format(n || 0);

const formatPct = (n) => `${((n || 0) * 100).toFixed(1)}%`;

const StaffContributionTable = ({ data, loading }) => {
  const [exporting, setExporting] = useState(false);

  const handleExport = () => {
    if (!data?.rows?.length) return;
    setExporting(true);
    try {
      const sheetData = data.rows.map((r) => ({
        'Nhân viên': r.employee_name,
        'Chi nhánh': r.branch_name || '—',
        'Đơn phục vụ': r.orders_served,
        'Đơn khách loyal': r.loyal_orders_served,
        'Tỉ lệ loyal': formatPct(r.loyal_ratio),
        'Doanh thu khách loyal (VND)': r.loyal_revenue,
      }));
      const ws = XLSX.utils.json_to_sheet(sheetData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Đóng góp NV');
      const wbOut = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const fname = `staff-loyal-contribution_${data.period.from_date}_${data.period.to_date}.xlsx`;
      saveAs(new Blob([wbOut], { type: 'application/octet-stream' }), fname);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <div>
          <Badge bg="warning" className="me-2">
            <Award size={14} className="me-1" />
            {data?.loyal_customer_count || 0} khách loyal (≥ {data?.loyal_threshold || '?'} lần)
          </Badge>
          <span className="text-muted small">
            Bảng này là cơ sở để admin chia thưởng cuối tháng — nhân viên phục vụ nhiều khách quay lại = đóng góp tốt.
          </span>
        </div>
        <Button
          size="sm"
          variant="outline-success"
          onClick={handleExport}
          disabled={!data?.rows?.length || exporting}
        >
          <Download size={14} className="me-1" />
          Xuất Excel
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" size="sm" /> <span className="ms-2">Đang tải…</span>
        </div>
      ) : !data?.rows?.length ? (
        <div className="text-center py-5 text-muted">Không có dữ liệu nhân viên trong khoảng thời gian.</div>
      ) : (
        <div className="table-responsive">
          <Table hover className="align-middle">
            <thead className="table-light">
              <tr>
                <th>#</th>
                <th>Nhân viên</th>
                <th>Chi nhánh</th>
                <th className="text-end">Đơn phục vụ</th>
                <th className="text-end">Đơn khách loyal</th>
                <th className="text-center">Tỉ lệ loyal</th>
                <th className="text-end">Doanh thu khách loyal</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r, idx) => (
                <tr key={r.employee_id || idx}>
                  <td className="text-muted">{idx + 1}</td>
                  <td className="fw-semibold">{r.employee_name}</td>
                  <td className="small text-muted">{r.branch_name || '—'}</td>
                  <td className="text-end">{r.orders_served.toLocaleString('vi-VN')}</td>
                  <td className="text-end">{r.loyal_orders_served.toLocaleString('vi-VN')}</td>
                  <td className="text-center">
                    <Badge bg={r.loyal_ratio >= 0.3 ? 'success' : r.loyal_ratio >= 0.1 ? 'primary' : 'secondary'}>
                      {formatPct(r.loyal_ratio)}
                    </Badge>
                  </td>
                  <td className="text-end fw-semibold">{formatCurrency(r.loyal_revenue)} ₫</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
    </div>
  );
};

export default StaffContributionTable;
